import React from 'react'
import { useConfiguratorContext } from '@augmatica/iframe-interface-react'

import { setIsEditItemPanelActive } from '../../../store/ui/uiSlice'
import { hasCompleteMallisData } from '../../../helpers/hasCompleteMallisData'

import { CardMedia, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import * as commonStyles from '../styles'
import * as styles from './styles'
import { useAppDispatch, useAppSelector } from '../../../hooks'

const Styled = {
  ...commonStyles,
  ...styles,
}

interface Props {
  itemsWithInfo: Array<{ id: string }>
  itemType: string
}

const ItemsAccordion: React.FC<Props> = ({ itemsWithInfo, itemType }) => {
  const configurator = useConfiguratorContext()
  const dispatch = useAppDispatch()

  const { assetBaseUrl, content } = useAppSelector((state) => state.init) || {}
  const { selectedItem } = useAppSelector((state) => state.selectedItem) || {}
  const { isEditItemPanelActive } = useAppSelector((state) => state.ui) || {}
  const { erpItems, lfcItems } = useAppSelector((state) => state.items) || { erpItems: [], lfcItems: [] }

  // Return null (explicit) when required data is missing to avoid returning undefined
  if (!assetBaseUrl && !content && !selectedItem) return null

  const getArticleAttributes = (article: any): any[] => {
    const desc =
      article?.configData?.configResult?.configProducts?.[0]?.orderRowDescription
    if (!desc) return []
    return desc.split(',').map((dimension: string) => dimension.split(':'))
  }

  const getEditorials = (article: any): any => {
    const list = Array.isArray(erpItems) ? erpItems : []
    const matchingErpItem = list.find((item: any) => item.Sku === article.erpItemNumber)
    // Always return an object merging the article with any matching ERP fields
    return { ...article, ...(matchingErpItem || {}) }
  }

  const handleChangeButtonClick = async (item: { id: string }): Promise<void> => {
    await configurator.selectItem(item.id)
    configurator.cameraToSelected()
    dispatch(setIsEditItemPanelActive(true))
  }

  return (
    <>
      {!itemsWithInfo.length ? (
        <Styled.Value>{content?.summary_panel?.configuration?.[`no_${itemType}s`]}</Styled.Value>
      ) : (
        itemsWithInfo.map((item) => {
          const editorial = getEditorials(item) || {}
          const panelId = `panel-${item.id}`
          const canChangeItem =
            !editorial.configData ||
            hasCompleteMallisData(
              { contextId: editorial.configData.contextId, erpItemNumber: editorial.erpItemNumber },
              lfcItems,
              erpItems
            )

          return (
            <Accordion
              key={item.id}
              sx={{
                boxShadow: 'none',
                // ensure the ::before used by MUI stays visible when expanded
                '&.Mui-expanded::before': {
                  opacity: '1',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${panelId}-content`}
                id={`${panelId}-header`}
                sx={{
                  alignItems: 'center',
                  backgroundColor: 'transparent',
                  // Smooth the summary content and add a slight delay
                  '& .MuiAccordionSummary-content': {
                    transition:
                      'opacity 220ms cubic-bezier(0.4,0,0.2,1) 60ms, transform 220ms cubic-bezier(0.4,0,0.2,1) 60ms',
                  },
                  // Smooth the expand icon rotation with a small delay
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    transition: 'transform 220ms cubic-bezier(0.4,0,0.2,1) 60ms',
                    padding: '6px',
                    '& svg': { color: 'primary.main' },
                  },
                  // Add a slight transition to the collapse (details) that follows the summary
                  '& + .MuiCollapse-root': {
                    transition: 'height 300ms cubic-bezier(0.4,0,0.2,1) 80ms',
                  },
                }}
              >
                {/* Ensure the expanded-accordion pseudo ::before remains visible */}
                <style>{`.MuiAccordion-root.Mui-expanded::before { opacity: 1 !important; }`}</style>
                <CardMedia
                  component="img"
                  alt={editorial.Title || editorial.Sku}
                  // For refrence see this link https://mui.com/system/getting-started/the-sx-prop/
                  sx={{
                    width: 100,
                    height: 'auto',
                    m: '0 0 auto 0',
                    pl: 0,
                    // fade image a bit during toggle for smoother appearance
                    transition: 'opacity 220ms cubic-bezier(0.4,0,0.2,1) 60ms',
                  }}
                  image={`${assetBaseUrl}/mediaarchive/s/images/${
                    editorial?.Title?.replaceAll('/', '-').replaceAll(' ', '-') || editorial.Sku
                  }/${editorial.Sku}?d=240x240`}
                />
                <Styled.AccordionSummaryTexts>
                  <Typography variant="subtitle2" sx={{ textTransform: 'none' }}>
                    {editorial.Title || editorial.Sku}
                  </Typography>
                  <Typography variant="caption">{editorial.TitleLong}</Typography>

                  {canChangeItem && (
                    <Styled.ChangeButton
                      onClick={() => {
                        handleChangeButtonClick(item).catch(console.error)
                      }}
                      aria-label={content?.summary_panel?.configuration.change_button || 'Change item'}
                      data-testid="change-item"
                    >
                      {content?.summary_panel?.configuration?.change_button}
                    </Styled.ChangeButton>
                  )}
                </Styled.AccordionSummaryTexts>
              </AccordionSummary>
              <AccordionDetails id={`${panelId}-content`}>
                {!isEditItemPanelActive && (
                  <Styled.Attributes>
                    {getArticleAttributes(item).map((attribute, attrIndex) => (
                      <Styled.ListItem key={`${attribute[0]}-${attrIndex}`}>
                        <Typography variant="subtitle2" sx={{ textTransform: 'none' }}>
                          {attribute[0]}
                        </Typography>
                        <Typography variant="body2">{attribute[1]}</Typography>
                      </Styled.ListItem>
                    ))}
                  </Styled.Attributes>
                )}
              </AccordionDetails>
            </Accordion>
          )
        })
      )}
    </>
  )
}

export default ItemsAccordion
